<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import useCountrySwitcher from '@/composables/useCountrySwitcher';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

const props = defineProps({
  rowClass: {
    type: String,
    default: '',
  },
  iconClass: {
    type: String,
    default: '',
  },
});

const { changeCountry, availableCountries } = useCountrySwitcher();
const { websiteText } = useWebsiteTexts();

const selectedCountry = ref<any>(null);
const selectedLanguage = ref<any>(null);

const selectedCountryId = ref('');
const selectedLanguageId = ref('');

const initialCountryId = ref('');
const initialLanguageId = ref('');


watch(availableCountries, (countries) => {
  if (countries?.length) {
    const current = countries.find(c => c.current) || countries[0];
    selectedCountry.value = current;
    selectedCountryId.value = current.id;
    initialCountryId.value = current.id;

    const currentLang = current.availableLanguages.find(l => l.current) || current.availableLanguages[0];
    selectedLanguage.value = currentLang;
    selectedLanguageId.value = currentLang.languageId;
    initialLanguageId.value = currentLang.languageId;
  }
}, { immediate: true });

watch(selectedCountryId, (id) => {
  const country = availableCountries.value.find(c => c.id === id);
  if (country) {
    selectedCountry.value = country;

    const currentLang = country.availableLanguages.find(l => l.current) || country.availableLanguages[0];
    selectedLanguage.value = currentLang;
    selectedLanguageId.value = currentLang.languageId;
  }
});


watch(selectedLanguageId, (id) => {
  const lang = selectedCountry.value?.availableLanguages.find(l => l.languageId === id);
  if (lang) selectedLanguage.value = lang;
});


const showConfirmButton = computed(() => {
  return selectedCountryId.value !== initialCountryId.value || selectedLanguageId.value !== initialLanguageId.value;
});


async function onConfirmChange() {
  try {
    const redirectUrl = await changeCountry(selectedCountryId.value, selectedLanguageId.value);
    if (redirectUrl) {
      window.location.assign(redirectUrl);
    } else {
      window.location.reload();
    }
  } catch (err) {
    alert(err);
  }
}

const getCountryDisplayName = (country: any) => country.displayName || country.channelName;
const getLanguageDisplayName = (language: any) => language.name || language.languageId;
</script>

<template>
  <div class="country-switcher" v-if="availableCountries.length > 1">
    <div class="country-switcher__top-container">
      <div class="country-switcher__titles">
        <h2 class="channel-switcher__title">
          {{websiteText('countryswitcher__changelocation').value}}
      </h2>
      <span>{{websiteText('countryswitcher__choosepreference').value}}</span>
      </div>
      <span class="country-switcher__title">{{websiteText('countryswitcher__country').value}}</span>
      <select :class="['country-switcher__select', rowClass]" v-model="selectedCountryId">
        <option v-for="country in availableCountries" :key="country.id" :value="country.id">
          {{ getCountryDisplayName(country) }}
        </option>
      </select>

      <span class="country-switcher__title">{{websiteText('countryswitcher__language').value}}</span>
      <select v-if="selectedCountry?.availableLanguages?.length" :class="['country-switcher__select', rowClass]"
        v-model="selectedLanguageId">
        <option v-for="language in selectedCountry.availableLanguages" :key="language.languageId"
          :value="language.languageId">
          {{ getLanguageDisplayName(language) }}
        </option>
      </select>
    </div>
    <div class="country-switcher__bottom-container">
      <button v-if="showConfirmButton" @click="onConfirmChange" class="confirm-btn">
        {{websiteText('countryswitcher__location').value}}
      </button>
    </div>
  </div>
</template>



<style>
.country-switcher {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.country-switcher__top-container {
  display: flex;
  flex-direction: column;
}

.country-switcher__titles {
  text-align: center;
  margin-bottom: 40px;
}

.country-switcher__bottom-container {
  display: flex;
  flex-direction: column;
}

.country-switcher__title {
  margin-bottom: 8px;
  font-size: 12px;
}

.country-switcher__select {
  font-family: "Mulish Medium", Arial, Helvetica, sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  border: 1px solid var(--color-gray-1440);
  background-color: white;
  color: var(--color-black);
  margin-bottom: 20px;
}
</style>
